import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { darken, useTheme } from '@mui/material/styles';
import { EM_DASH } from '../../utils/formatDate';

/**
 * Pill — shared pale pill used by StatusPill and CategoryPill
 * (UI Design Standards §4.3). Internal to Components/ui.
 * `colors` is a token group: { bg, text, border, borderStyle? } (solid by default).
 */
export const Pill = ({ colors, label, icon, onClick, title, sx, ...chipProps }) => {
  const { ft } = useTheme().palette;

  if (label === null || label === undefined || label === '') {
    return <Box component="span">{EM_DASH}</Box>;
  }

  const hoverBorder =
    colors.border === 'transparent' ? ft.neutrals.divider : darken(colors.border, 0.15);

  const chip = (
    <Chip
      size="small"
      label={label}
      icon={icon}
      onClick={onClick}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 500,
        textTransform: 'none',
        borderRadius: `${ft.radius.pill}px`,
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px ${colors.borderStyle || 'solid'} ${colors.border}`,
        '& .MuiChip-label': { px: 1 },
        '& .MuiChip-icon': {
          color: colors.text,
          fontSize: 14,
          ml: 0.75,
          mr: -0.5,
        },
        '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible': {
          backgroundColor: colors.bg,
          borderColor: hoverBorder,
        },
        '&.MuiChip-clickable:active': { boxShadow: 'none' },
        ...sx,
      }}
      {...chipProps}
    />
  );

  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
};

const TONES = ['success', 'warning', 'error', 'info', 'neutral', 'neutralOutline'];

/**
 * StatusPill — pale tone pill. Never uppercase, never a saturated fill.
 *
 * Props:
 *   tone     'success' | 'warning' | 'error' | 'info' | 'neutral' (default)
 *            | 'neutralOutline' (white, dashed border: not required / skipped / no bid)
 *   label    {node}   empty or null renders "—"
 *   icon     {element} optional leading icon (Outlined), sized 14px
 *   onClick  {func}   optional; makes the pill clickable
 *   title    {node}   optional tooltip
 */
const StatusPill = ({ tone = 'neutral', ...props }) => {
  const { ft } = useTheme().palette;
  const colors = ft.tones[TONES.includes(tone) ? tone : 'neutral'];
  return <Pill colors={colors} {...props} />;
};

export default StatusPill;
